import { createContext, useContext, useEffect, useState } from "react";

/**
 * Global weight-unit setting. Everything in MongoDB is stored in kg.
 * This context simply provides display-side conversion + formatting so the UI
 * can flip between "kg" and "lb" without touching the database.
 */

const KG_PER_LB = 0.453592;        // 1 lb = 0.453592 kg
const LB_PER_KG = 1 / KG_PER_LB;   // 1 kg ≈ 2.20462 lb
const STORAGE_KEY = "uptop-weight-unit";

// Locales that traditionally use pounds for body weight.
// US, Liberia, Myanmar still use imperial; UK uses stones/lb colloquially for body weight.
const LB_LOCALES = new Set(["en-US", "en-GB", "en-LR", "en-MM"]);
const LB_LANGUAGE_REGIONS = new Set(["US", "GB", "LR", "MM"]);

function detectDefaultUnit() {
  if (typeof navigator === "undefined") return "kg";
  const candidates = []
    .concat(navigator.languages || [])
    .concat(navigator.language ? [navigator.language] : []);
  for (const raw of candidates) {
    if (!raw) continue;
    if (LB_LOCALES.has(raw)) return "lb";
    const region = raw.split("-")[1]?.toUpperCase();
    if (region && LB_LANGUAGE_REGIONS.has(region)) return "lb";
  }
  return "kg";
}

const round = (n, decimals) => {
  const f = Math.pow(10, decimals);
  return Math.round(n * f) / f;
};

const WeightUnitContext = createContext({
  unit: "kg",
  setUnit: () => {},
  fromKg: (kg) => Number(kg) || 0,
  toKg: (v) => Number(v) || 0,
  label: "kg",
  formatWeight: (kg) => `${kg} kg`,
});

export function WeightUnitProvider({ children }) {
  const [unit, setUnitState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "lb" || saved === "kg") return saved;
      // First-ever visit: infer from locale, but don't persist yet —
      // we only lock the choice once the user explicitly picks in Settings.
      return detectDefaultUnit();
    } catch (e) {
      return "kg";
    }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, unit); } catch (e) { /* ignore */ }
  }, [unit]);

  const fromKg = (kg) => {
    const n = Number(kg);
    if (!Number.isFinite(n)) return 0;
    return unit === "lb" ? n * LB_PER_KG : n;
  };

  const toKg = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return unit === "lb" ? n * KG_PER_LB : n;
  };

  const formatWeight = (kg, { decimals = 1, withUnit = true, locale = true } = {}) => {
    if (kg == null || kg === "" || Number.isNaN(Number(kg))) return "—";
    const converted = round(fromKg(kg), decimals);
    const text = locale ? converted.toLocaleString(undefined, { maximumFractionDigits: decimals }) : String(converted);
    return withUnit ? `${text} ${unit}` : text;
  };

  // For input fields: take the user's stored kg value, return a string in the active unit
  const toInputValue = (kg, decimals = 1) => {
    if (kg == null || kg === "" || Number.isNaN(Number(kg))) return "";
    return String(round(fromKg(kg), decimals));
  };

  return (
    <WeightUnitContext.Provider
      value={{ unit, setUnit: setUnitState, fromKg, toKg, label: unit, formatWeight, toInputValue }}
    >
      {children}
    </WeightUnitContext.Provider>
  );
}

export const useWeightUnit = () => useContext(WeightUnitContext);
